import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs, 
  getDoc,
  doc,
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import {
  getAnonymousWorksheets,
  getAnonymousWorksheet,
  saveAnonymousWorksheet,
  updateAnonymousWorksheet,
  deleteAnonymousWorksheet,
  toggleAnonymousFavorite,
  getAnonymousFavorites
} from '../utils/anonymousWorksheet';

// 학습지 컨텍스트 생성
const WorksheetContext = createContext();

// 학습지 컨텍스트 프로바이더 컴포넌트
export const WorksheetProvider = ({ children }) => {
  const { currentUser, userDetails, isAnonymous } = useAuth();
  const [worksheets, setWorksheets] = useState([]);
  const [favoriteWorksheets, setFavoriteWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeWorksheet, setActiveWorksheet] = useState(null);

  // 학습지 목록 로드 (익명/로그인 사용자 구분)
  useEffect(() => {
    setLoading(true);
    setError(null);
    
    let unsubscribe = null;
    
    try {
      if (isAnonymous) {
        // 익명 사용자의 경우 로컬 스토리지에서 데이터 로드
        const anonWorksheets = getAnonymousWorksheets();
        setWorksheets(anonWorksheets);
        
        // 즐겨찾기 학습지 로드
        const favorites = getAnonymousFavorites();
        const favoriteWs = anonWorksheets.filter(ws => favorites.includes(ws.id));
        setFavoriteWorksheets(favoriteWs);
        setLoading(false);
      } else if (currentUser) {
        // 로그인 사용자의 경우 Firestore에서 데이터 로드 (실시간 리스너 사용)
        const q = query(
          collection(db, 'worksheets'),
          where('userId', '==', currentUser.uid),
          orderBy('updatedAt', 'desc')
        );
        
        // onSnapshot을 사용하여 실시간 리스너 설정
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          const worksheetList = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            // ISO 문자열 형식으로 변환 (클라이언트에서 일관된 처리를 위해)
            createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
          }));
          
          setWorksheets(worksheetList);
          
          // 즐겨찾기 학습지 필터링 (stats.favoriteCount > 0인 학습지)
          const favoriteWs = worksheetList.filter(ws => {
            return ws.favorite || (ws.stats && ws.stats.favoriteCount > 0);
          });
          setFavoriteWorksheets(favoriteWs);
          setLoading(false);
        }, (err) => {
          console.error('학습지 실시간 구독 오류:', err);
          setError('학습지를 불러오는 중 오류가 발생했습니다.');
          setLoading(false);
        });
      }
    } catch (err) {
      console.error('학습지 로드 오류:', err);
      setError('학습지를 불러오는 중 오류가 발생했습니다.');
      setLoading(false);
    }

    // 컴포넌트 언마운트 시 리스너 해제
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser, isAnonymous]);

  // 개별 학습지 로드
  const getWorksheet = useCallback(async (worksheetId) => {
    if (!worksheetId) return null;
    
    try {
      if (isAnonymous) {
        // 익명 사용자의 경우 먼저 로컬 스토리지에서 데이터 로드 시도
        const localWorksheet = getAnonymousWorksheet(worksheetId);
        if (localWorksheet) {
          return localWorksheet;
        }
        
        // 로컬에 없으면 Firestore에서 공개 학습지 조회
        try {
          const docRef = doc(db, 'worksheets', worksheetId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const data = docSnap.data();
            // 공개 학습지만 접근 허용
            if (data.isPublic) {
              return {
                id: docSnap.id,
                ...data,
                createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
                updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
              };
            }
          }
        } catch (error) {
          console.error('익명 사용자의 Firestore 학습지 조회 오류:', error);
        }
        
        return null;
      } else if (currentUser) {
        // 로그인 사용자의 경우 Firestore에서 데이터 로드
        const docRef = doc(db, 'worksheets', worksheetId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
          };
        }
      }
      return null;
    } catch (err) {
      console.error('학습지 조회 오류:', err);
      throw new Error('학습지를 불러오는 중 오류가 발생했습니다.');
    }
  }, [currentUser, isAnonymous]);

  // 학습지 저장
  const saveWorksheet = useCallback(async (worksheetData) => {
    try {
      if (isAnonymous) {
        // 익명 사용자의 경우 로컬 스토리지에 저장
        return saveAnonymousWorksheet({
          ...worksheetData,
          creator: '익명 사용자',
          isPublic: true // 익명 사용자의 학습지도 공개 상태로 설정
        });
      } else if (currentUser) {
        // 로그인 사용자의 경우 Firestore에 저장
        // id 를 먼저 정해 setDoc 으로 쓴다 — addDoc('없을 때만 만든다')과 달리 응답을 못 받은 재시도가 already-exists 로 실패하지 않는다.
        const docRef = doc(collection(db, 'worksheets'));
        const worksheetDoc = {
          ...worksheetData,
          userId: currentUser.uid,
          creator: userDetails?.displayName || currentUser.displayName || '사용자',
          isPublic: true, // 모든 학습지를 공개 상태로 설정
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          stats: {
            viewCount: 0,
            downloadCount: 0,
            favoriteCount: 0,
            copyCount: 0,
            shareCount: 0
          }
        };
        try {
          await setDoc(docRef, worksheetDoc);
        } catch (_) {
          await new Promise((resolve) => setTimeout(resolve, 600));
          await setDoc(docRef, worksheetDoc);
        }
        
        // 학습지 목록 업데이트
        const newWorksheet = {
          id: docRef.id,
          ...worksheetData,
          userId: currentUser.uid,
          creator: userDetails?.displayName || currentUser.displayName || '사용자',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isPublic: true, // 클라이언트 상태도 업데이트
          stats: {
            viewCount: 0,
            downloadCount: 0,
            favoriteCount: 0,
            copyCount: 0,
            shareCount: 0
          }
        };
        
        setWorksheets(prev => [newWorksheet, ...prev]);
        
        return { id: docRef.id, success: true };
      }
      
      return { success: false, error: '사용자 인증 정보가 없습니다.' };
    } catch (err) {
      console.error('학습지 저장 오류:', err);
      return { success: false, error: err.message };
    }
  }, [currentUser, isAnonymous, userDetails]);

  // 학습지 업데이트
  const updateWorksheet = useCallback(async (worksheetId, worksheetData) => {
    try {

      
      if (!worksheetId) {
        return { success: false, error: '학습지 ID가 없습니다.' };
      }
      
      if (isAnonymous) {
        // 익명 사용자의 경우 로컬 스토리지 업데이트

        return updateAnonymousWorksheet(worksheetId, worksheetData);
      } else if (currentUser) {
        // 로그인 사용자의 경우 Firestore 업데이트
        
        const docRef = doc(db, 'worksheets', worksheetId);
        
        // 업데이트할 데이터 준비
        const updateData = {
          ...worksheetData,
          updatedAt: serverTimestamp()
        };
        
        
        
        await updateDoc(docRef, updateData);
        
        
        // 클라이언트에서 사용할 업데이트 시간 생성
        const updatedTimestamp = new Date().toISOString();
        
        // 업데이트된 문서를 다시 가져와서 최신 데이터 확인
        
        const updatedDocSnap = await getDoc(docRef);
        const updatedData = updatedDocSnap.exists() 
          ? {
              id: updatedDocSnap.id,
              ...updatedDocSnap.data(),
              updatedAt: updatedTimestamp
            }
          : null;
        
        
        
        // 학습지 목록 업데이트
        setWorksheets(prev => prev.map(ws => {
          if (ws.id === worksheetId) {
            return {
              ...ws,
              ...worksheetData,
              updatedAt: updatedTimestamp
            };
          }
          return ws;
        }));
        
        
        
        // 성공 및 업데이트된 데이터 반환
        return { 
          success: true, 
          id: worksheetId, 
          data: updatedData || {
            ...worksheetData,
            id: worksheetId,
            updatedAt: updatedTimestamp
          }
        };
      }
      
      return { success: false, error: '사용자 인증 정보가 없습니다.' };
    } catch (err) {
      console.error('학습지 업데이트 오류:', err);
      return { success: false, error: err.message };
    }
  }, [currentUser, isAnonymous]);

  // 학습지 삭제
  const deleteWorksheet = useCallback(async (worksheetId) => {
    try {
      if (!worksheetId) {
        return { success: false, error: '학습지 ID가 없습니다.' };
      }
      
      if (isAnonymous) {
        // 익명 사용자의 경우 로컬 스토리지에서 삭제
        return deleteAnonymousWorksheet(worksheetId);
      } else if (currentUser) {
        // 로그인 사용자의 경우 Firestore에서 삭제
        const docRef = doc(db, 'worksheets', worksheetId);
        await deleteDoc(docRef);
        
        // 학습지 목록 업데이트
        setWorksheets(prev => prev.filter(ws => ws.id !== worksheetId));
        setFavoriteWorksheets(prev => prev.filter(ws => ws.id !== worksheetId));
        
        return { success: true };
      }
      
      return { success: false, error: '사용자 인증 정보가 없습니다.' };
    } catch (err) {
      console.error('학습지 삭제 오류:', err);
      return { success: false, error: err.message };
    }
  }, [currentUser, isAnonymous]);

  // 즐겨찾기 토글
  const toggleFavorite = useCallback(async (worksheetId) => {
    try {
      if (!worksheetId) {
        return { success: false, error: '학습지 ID가 없습니다.' };
      }
      
      if (isAnonymous) {
        // 익명 사용자의 경우 로컬 스토리지에서 즐겨찾기 토글
        const result = toggleAnonymousFavorite(worksheetId);
        
        // 즐겨찾기 목록 업데이트
        if (result.success) {
          setWorksheets(prev => prev.map(ws => {
            if (ws.id === worksheetId) {
              return { ...ws, favorite: result.isFavorite };
            }
            return ws;
          }));
          
          // 즐겨찾기 목록 업데이트
          if (result.isFavorite) {
            // 즐겨찾기에 추가
            const worksheet = worksheets.find(ws => ws.id === worksheetId);
            if (worksheet) {
              setFavoriteWorksheets(prev => [{ ...worksheet, favorite: true }, ...prev]);
            }
          } else {
            // 즐겨찾기에서 제거
            setFavoriteWorksheets(prev => prev.filter(ws => ws.id !== worksheetId));
          }
        }
        
        return result;
      } else if (currentUser) {
        // 로그인 사용자의 경우 Firestore에서 즐겨찾기 토글
        const docRef = doc(db, 'worksheets', worksheetId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const worksheetData = docSnap.data();
          const currentStats = worksheetData.stats || { 
            viewCount: 0, 
            downloadCount: 0, 
            favoriteCount: 0 
          };
          
          // 현재 즐겨찾기 상태 확인
          const isFavorite = !!(worksheetData.favorite);
          
          // 반대 상태로 토글
          const newFavorite = !isFavorite;
          
          // 즐겨찾기 상태 및 카운트 업데이트
          await updateDoc(docRef, {
            favorite: newFavorite,
            'stats.favoriteCount': newFavorite 
              ? (currentStats.favoriteCount || 0) + 1 
              : Math.max((currentStats.favoriteCount || 0) - 1, 0),
            updatedAt: serverTimestamp()
          });
          
          // 학습지 목록 업데이트
          setWorksheets(prev => prev.map(ws => {
            if (ws.id === worksheetId) {
              const newStats = { 
                ...(ws.stats || {}), 
                favoriteCount: newFavorite 
                  ? ((ws.stats?.favoriteCount || 0) + 1) 
                  : Math.max((ws.stats?.favoriteCount || 0) - 1, 0) 
              };
              
              return { 
                ...ws, 
                favorite: newFavorite,
                stats: newStats,
                updatedAt: new Date().toISOString()
              };
            }
            return ws;
          }));
          
          // 즐겨찾기 목록 업데이트
          if (newFavorite) {
            // 즐겨찾기에 추가
            const worksheet = worksheets.find(ws => ws.id === worksheetId);
            if (worksheet) {
              setFavoriteWorksheets(prev => [{ 
                ...worksheet, 
                favorite: true,
                stats: { 
                  ...(worksheet.stats || {}), 
                  favoriteCount: (worksheet.stats?.favoriteCount || 0) + 1 
                }
              }, ...prev]);
            }
          } else {
            // 즐겨찾기에서 제거
            setFavoriteWorksheets(prev => prev.filter(ws => ws.id !== worksheetId));
          }
          
          return { success: true, isFavorite: newFavorite };
        }
        
        return { success: false, error: '학습지를 찾을 수 없습니다.' };
      }
      
      return { success: false, error: '사용자 인증 정보가 없습니다.' };
    } catch (err) {
      console.error('즐겨찾기 토글 오류:', err);
      return { success: false, error: err.message };
    }
  }, [currentUser, isAnonymous, worksheets]);

  // 공개 학습지 검색
  const searchPublicWorksheets = useCallback(async (searchParams) => {
    try {
      if (!searchParams) {
        return { success: false, error: '검색 조건이 없습니다.' };
      }
      
      // 기본 쿼리: 공개된 학습지만 조회
      let q = query(
        collection(db, 'worksheets'),
        where('isPublic', '==', true)
      );
      
      // 검색 조건 적용
      if (searchParams.subject) {
        q = query(q, where('subject', '==', searchParams.subject));
      }
      
      if (searchParams.grade) {
        q = query(q, where('grade', '==', searchParams.grade));
      }
      
      if (searchParams.category) {
        q = query(q, where('tags', 'array-contains', searchParams.category));
      }
      
      // 정렬 조건 적용
      if (searchParams.orderBy) {
        const orderField = searchParams.orderBy === 'popular' 
          ? 'stats.viewCount' 
          : 'createdAt'; // ✅ 수정: latest일 때 createdAt 사용
        
        const orderDirection = searchParams.orderBy === 'latest' 
          ? 'desc' 
          : 'desc'; // 인기순, 최신순 모두 내림차순
        
        q = query(q, orderBy(orderField, orderDirection));
      } else {
        // ✅ 수정: 기본 정렬도 createdAt 기준으로 변경
        q = query(q, orderBy('createdAt', 'desc'));
      }
      
      // 결과 제한
      q = query(q, limit(searchParams.limit || 20));
      
      // 쿼리 실행
      const querySnapshot = await getDocs(q);
      const worksheetList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
      }));
      
      return { success: true, data: worksheetList };
    } catch (err) {
      console.error('공개 학습지 검색 오류:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // 마이그레이션 수행 함수
  const migrateData = useCallback(async () => {
    if (!currentUser || isAnonymous) {
      return { success: false, message: '로그인 후 사용할 수 있습니다.' };
    }
    
    // migrateAnonymousWorksheets 관련 로직이 제거되었으므로,
    // 이 함수는 현재 아무 작업도 하지 않음.
    // 추후 다른 마이그레이션 로직이 필요할 경우 여기에 추가.
    return { success: true, message: '마이그레이션할 데이터가 없습니다.' };
  }, [currentUser, isAnonymous]);

  // 공개 학습지를 내 학습지로 복사
  const copyWorksheetToMyCollection = useCallback(async (worksheetId) => {
    try {
      if (!worksheetId) {
        return { success: false, error: '학습지 ID가 없습니다.' };
      }
      
      // 원본 학습지 데이터 가져오기
      const originalWorksheet = await getWorksheet(worksheetId);
      if (!originalWorksheet) {
        return { success: false, error: '복사할 학습지를 찾을 수 없습니다.' };
      }
      
      // 원본 학습지 데이터에서 필요한 필드만 추출
      const { 
        title, subject, grade, description, contents, 
        questions, viewMode, categories, options, search_info,
        free_content, summary, type, topics, performance_content,
        curriculum_info, achievementStandards, extracted_metadata, metadata, form_selections
      } = originalWorksheet;
      
      // 새 학습지 데이터 준비
      const newWorksheetData = {
        title: title || '무제',
        subject: subject || '',
        grade: grade || '',
        description: description || '',
        contents: contents || '',
        questions: questions || [],
        viewMode: viewMode || 'student',
        categories: categories || [],
        options: options || {},
        originalWorksheetId: worksheetId,
        copiedFrom: worksheetId,
        search_info: search_info || {}, // 검색 정보 복사
        ...(free_content && { free_content }), // 자유형 학습지 내용
        ...(performance_content && { performance_content }), // 🆕 수행평가 설계 내용
        ...(summary && { summary }),
        ...(type && { type }),
        ...(topics && { topics }),
        ...(curriculum_info && { curriculum_info }),
        ...(achievementStandards && { achievementStandards }),
        ...(extracted_metadata && { extracted_metadata }),
        ...(metadata && { metadata }),
        ...(form_selections && { form_selections })
      };
      
      // 내 학습지로 저장
      const result = await saveWorksheet(newWorksheetData);
      
      if (result.success) {
        // 원본 학습지의 복사 카운트 증가
        try {
          // 익명 사용자의 학습지는 업데이트 불가능
          if (originalWorksheet.userId) {
            const docRef = doc(db, 'worksheets', worksheetId);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
              const worksheetData = docSnap.data();
              const currentStats = worksheetData.stats || { 
                viewCount: 0, 
                downloadCount: 0, 
                favoriteCount: 0,
                copyCount: 0
              };
              
              await updateDoc(docRef, {
                'stats.copyCount': (currentStats.copyCount || 0) + 1,
                updatedAt: serverTimestamp()
              });
            }
          }
        } catch (err) {
          console.error('복사 카운트 업데이트 오류:', err);
          // 복사 카운트 업데이트 실패해도 복사 자체는 성공한 것으로 처리
        }
      }
      
      return result;
    } catch (err) {
      console.error('학습지 복사 오류:', err);
      return { success: false, error: err.message };
    }
  }, [getWorksheet, saveWorksheet]);

  // 학습지 조회 시 조회 수 증가 함수
  const incrementViewCount = useCallback(async (worksheetId) => {
    try {
      if (!worksheetId) {
        return { success: false, error: '학습지 ID가 없습니다.' };
      }
      
      // 조회수 증가는 Firestore 학습지에만 적용
      // 익명 사용자의 로컬 학습지는 영향 없음
      
      // 학습지 문서 참조
      const docRef = doc(db, 'worksheets', worksheetId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const worksheetData = docSnap.data();
        
        // 학습지가 존재하면 조회수 증가
        const currentStats = worksheetData.stats || {
          viewCount: 0,
          downloadCount: 0,
          favoriteCount: 0,
          copyCount: 0,
          shareCount: 0
        };
        
        // Firestore에 조회수 업데이트
        await updateDoc(docRef, {
          'stats.viewCount': (currentStats.viewCount || 0) + 1
        });
        
        return { success: true };
      }
      
      return { success: false, error: '학습지를 찾을 수 없습니다.' };
    } catch (err) {
      console.error('조회수 증가 중 오류:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // 학습지 다운로드 시 다운로드 수 증가 함수
  const incrementDownloadCount = useCallback(async (worksheetId) => {
    try {
      if (!worksheetId) {
        return { success: false, error: '학습지 ID가 없습니다.' };
      }
      
      // 다운로드 수 증가는 Firestore 학습지에만 적용
      // 익명 사용자의 로컬 학습지는 영향 없음
      
      // 학습지 문서 참조
      const docRef = doc(db, 'worksheets', worksheetId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const worksheetData = docSnap.data();
        
        // 학습지가 존재하면 다운로드 수 증가
        const currentStats = worksheetData.stats || {
          viewCount: 0,
          downloadCount: 0,
          favoriteCount: 0,
          copyCount: 0,
          shareCount: 0
        };
        
        // Firestore에 다운로드 수 업데이트
        await updateDoc(docRef, {
          'stats.downloadCount': (currentStats.downloadCount || 0) + 1
        });
        
        return { success: true };
      }
      
      return { success: false, error: '학습지를 찾을 수 없습니다.' };
    } catch (err) {
      console.error('다운로드 수 증가 중 오류:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // 학습지 공유 시 공유 수 증가 함수
  const incrementShareCount = useCallback(async (worksheetId) => {
    try {
      if (!worksheetId) {
        return { success: false, error: '학습지 ID가 없습니다.' };
      }
      
      // 공유 수 증가는 Firestore 학습지에만 적용
      // 익명 사용자의 로컬 학습지는 영향 없음
      
      // 학습지 문서 참조
      const docRef = doc(db, 'worksheets', worksheetId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const worksheetData = docSnap.data();
        
        // 학습지가 존재하면 공유 수 증가
        const currentStats = worksheetData.stats || {
          viewCount: 0,
          downloadCount: 0,
          favoriteCount: 0,
          copyCount: 0,
          shareCount: 0
        };
        
        // Firestore에 공유 수 업데이트
        await updateDoc(docRef, {
          'stats.shareCount': (currentStats.shareCount || 0) + 1
        });
        
        return { success: true };
      }
      
      return { success: false, error: '학습지를 찾을 수 없습니다.' };
    } catch (err) {
      console.error('공유 수 증가 중 오류:', err);
      return { success: false, error: err.message };
    }
  }, []);

  return (
    <WorksheetContext.Provider
      value={{
        worksheets,
        favoriteWorksheets,
        loading,
        error,
        activeWorksheet,
        setActiveWorksheet,
        getWorksheet,
        saveWorksheet,
        updateWorksheet,
        deleteWorksheet,
        toggleFavorite,
        searchPublicWorksheets,
        migrateData,
        copyWorksheetToMyCollection,
        incrementViewCount,
        incrementDownloadCount,
        incrementShareCount
      }}
    >
      {children}
    </WorksheetContext.Provider>
  );
};

// 학습지 컨텍스트 훅
export const useWorksheet = () => useContext(WorksheetContext); 